import { PAGE_CONTROL_BAR_KEY } from '../../constants'
import { reportDataSetService } from '../report-data-set.service'
import { SUBGROUP_COLUMN } from '../report-data-set.constants'

class PageControlBarEditService {
  getPageControlBar (page, currentEdit) {
    // If editing a block that has changes to the page control bar
    if (currentEdit && (currentEdit.pageControlBar || currentEdit.shouldRemovePageControlBar)) {
      return currentEdit.pageControlBar
    }

    // If currently editing the page control bar
    if (currentEdit && currentEdit.block && currentEdit.block.pageControlBar) {
      return currentEdit.block.pageControlBar
    }

    // Otherwise, check the layout
    return page && page.layout && page.layout.pageControlBar
  }

  /**
   * Update the block and page level control bar configs based on the given selections
   */
  updatePageControlBar (pageControlBar, page, block, selections) {
    const updatedFilterList = this.getBlockUpdates(block, selections)

    // No changes
    if (updatedFilterList === undefined) {
      return { block, pageControlBar }
    }

    // Get updated block
    block = this.updateBlockParentControlBar(block, updatedFilterList)

    // Get updated page control bar
    const pageFilterSupersets = this.getPageFilterSupersets(page, block)
    pageControlBar = this.mergePageFilterLists(pageControlBar, pageFilterSupersets)

    // If all filters have been removed - remove the page control bar config
    if (pageControlBar.filterDefinition.fields.length === 0) {
      pageControlBar = undefined
    }

    return { block, pageControlBar }
  }

  getBlockUpdates (block, selections) {
    const parentControlBar = block.props.parentControlBar || {}
    const existing = (parentControlBar.key === PAGE_CONTROL_BAR_KEY)
      ? parentControlBar.filters || []
      : []
    const newFilters = selections
      .filter(({ column }) => column === SUBGROUP_COLUMN.PAGE_FILTER)
      .map(({ row }) => row)

    if (existing.length !== newFilters.length || newFilters.find((filter) => !existing.includes(filter))) {
      return newFilters
    }
  }

  updateBlockParentControlBar (block, newFilterList) {
    block = { ...block }
    block.props = { ...block.props }
    block.props.parentControlBar = newFilterList.length > 0
      ? {
        key: PAGE_CONTROL_BAR_KEY,
        filters: newFilterList
      }
      : undefined
    return block
  }

  /**
   * For each block that is controlled by the page, get the subgroup value options and combine them into a superset of
   * options for each filter.
   */
  getPageFilterSupersets (page, block) {
    return page.layout.blocks
      // replace the updated block in the page's list of blocks
      .map((b) => b.key === block.key ? block : b)

      // grab just the blocks that are controlled by the page filters
      .filter((b) => b.props && b.props.parentControlBar && b.props.parentControlBar.key === PAGE_CONTROL_BAR_KEY)

      // for each page filter, build a superset of the options across all datasets on the page
      .reduce((result, b) => {
        b.props.parentControlBar.filters.forEach((filter) => {
          const options = reportDataSetService.getSubgroupValueOptions(page, b.dataKey, filter)
          if (!result[filter]) {
            result[filter] = options
          } else {
            options.forEach((option) => {
              if (!result[filter].find(({ value }) => value === option.value)) {
                result[filter].push(option)
              }
            })
          }
        })
        return result
      }, {})
  }

  /**
   * Take the superset list and merge it into the existing page control bar config
   */
  mergePageFilterLists (pageControlBar, pageFilterSupersets) {
    const updatedPageFilters = Object.keys(pageFilterSupersets)

    pageControlBar = { ...(pageControlBar || {}) }
    pageControlBar.filterDefinition = { ...(pageControlBar.filterDefinition || {}) }
    pageControlBar.filterDefinition.fields = pageControlBar.filterDefinition.fields || []

    pageControlBar.filterDefinition.fields = pageControlBar.filterDefinition.fields
      // Filter out any existing filters that are not in the updated list
      .filter(({ key }) => updatedPageFilters.includes(key))

      // Merge existing filter values with new ones
      .map((field) => {
        field = { ...field }

        const superSetOptions = pageFilterSupersets[field.key]
        const superSetValues = superSetOptions.map(({ value }) => value)

        // remove existing options not in the new list
        field.options = field.options.filter(({ value }) => superSetValues.includes(value))

        // Add new values not already in the existing list
        superSetOptions.forEach((option) => {
          if (!field.options.find(({ value }) => value === option.value)) {
            field.options.push(option)
          }
        })

        return field
      })

    updatedPageFilters.forEach((filter) => {
      if (!pageControlBar.filterDefinition.fields.find(({ key }) => key === filter)) {
        pageControlBar.filterDefinition.fields.push(this.createNewFilter(filter, pageFilterSupersets[filter]))
      }
    })

    return pageControlBar
  }

  createNewFilter (key, options = []) {
    return { key, label: key, options, type: 'select' }
  }
}

export const pageControlBarEditService = new PageControlBarEditService()
